import { computed } from 'vue';
import { SitesLocalePrefix } from '../../../enums';
import { useRecommendationsStore } from '../../../routes/stores/use-recommendations-store';
import { storeToRefs } from 'pinia';
import {
  RecommendationType,
  type RecommendationVisual,
} from '@centric-os/types/api/recommendations';

export default (translate?: (key: string, params?: Record<string, string>) => string) => {
  const LOCALE_PREFIX = SitesLocalePrefix.SITE_OVERVIEW;

  const getStoreRefs = () => {
    const recommendationsStore = useRecommendationsStore();
    return storeToRefs(recommendationsStore);
  };

  const formatTickStrings = (label: string): string | string[] => {
    const MAX_LENGTH = 19;

    // split long labels into maximum 2 lines
    if (label && label.length > MAX_LENGTH) {
      const words = label.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        // Account for space when currentLine already has content
        const potentialLength = currentLine ? currentLine.length + 1 + word.length : word.length;
        if (potentialLength <= MAX_LENGTH) {
          currentLine += (currentLine ? ' ' : '') + word;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            if (lines.length === 2) break;
          }
          currentLine = word;
        }
      }

      if (currentLine && lines.length < 2) {
        lines.push(currentLine);
      } else if (currentLine && lines.length === 2) {
        // if there are remaining characters but already 2 lines, add ellipsis to second line
        const lastLine = lines[1];
        if (lastLine.length > MAX_LENGTH - 3) {
          lines[1] = lastLine.substring(0, MAX_LENGTH - 3) + '...';
        } else {
          lines[1] = lastLine + '...';
        }
      }

      return lines;
    }
    return label || '';
  };

  const parsedVisual = computed<RecommendationVisual | null>(() => {
    const storeRefs = getStoreRefs();
    const visual = storeRefs.currentRecommendation.value?.visual;
    if (!visual) return null;
    return visual as RecommendationVisual;
  });

  const formattedRevenuePctDiff = computed<string>(() => {
    const pctDiff = parsedVisual.value?.revenue_pct_diff;
    if (pctDiff === undefined) return '';
    return `${Math.round(pctDiff)}%`;
  });

  const recHeader = computed<string>(() => {
    if (!translate) return '';
    const storeRefs = getStoreRefs();
    switch (storeRefs.currentRecommendationType.value) {
      case RecommendationType.CPG:
        return translate(`${LOCALE_PREFIX}.recommendations.recHeader.cpg`);
      case RecommendationType.EXPAND:
        return translate(`${LOCALE_PREFIX}.recommendations.recHeader.expand`);
      case RecommendationType.REDUCE:
        return translate(`${LOCALE_PREFIX}.recommendations.recHeader.reduce`);
      default:
        return '';
    }
  });

  const recDescription = computed<string>(() => {
    if (!translate) return '';
    const storeRefs = getStoreRefs();
    const recType = storeRefs.currentRecommendationType.value;
    const hasPercent = parsedVisual.value?.revenue_pct_diff !== undefined;

    switch (recType) {
      case RecommendationType.CPG:
        return translate(`${LOCALE_PREFIX}.recommendations.recDescription.cpg`);

      case RecommendationType.EXPAND:
        return hasPercent
          ? translate(`${LOCALE_PREFIX}.recommendations.recDescription.expandWithPercent`, {
              percent: formattedRevenuePctDiff.value,
            })
          : translate(`${LOCALE_PREFIX}.recommendations.recDescription.expand`);

      case RecommendationType.REDUCE:
        return hasPercent
          ? translate(`${LOCALE_PREFIX}.recommendations.recDescription.reduceWithPercent`, {
              percent: formattedRevenuePctDiff.value,
            })
          : translate(`${LOCALE_PREFIX}.recommendations.recDescription.reduce`);

      default:
        return '';
    }
  });

  return {
    formatTickStrings,
    parsedVisual,
    formattedRevenuePctDiff,
    recHeader,
    recDescription,
  };
};
